import React, { useState, useEffect, useRef } from 'react';
import { TransactionDocumentsAPI, AuthAPI } from '../services/api';
import { TransactionDocument } from '../types/payment';
import { useAuth } from '../context/AuthContext';

interface DocumentDialogProps {
  recordId: string;
  isOpen: boolean;
  onClose: () => void;
  recordTitle?: string;  // Optional title for display
}

// Allowed file types
const ALLOWED_EXTENSIONS = ['.pdf', '.jpg', '.jpeg', '.png', '.gif', '.doc', '.docx', '.xls', '.xlsx', '.txt', '.csv'];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

export const DocumentDialog: React.FC<DocumentDialogProps> = ({ recordId, isOpen, onClose, recordTitle }) => {
  const { user, token } = useAuth();
  const [documents, setDocuments] = useState<TransactionDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [description, setDescription] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<TransactionDocument | null>(null);
  const [deleting, setDeleting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen && recordId) {
      loadDocuments();
    }
  }, [isOpen, recordId]);

  const loadDocuments = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await TransactionDocumentsAPI.getDocuments(recordId);
      setDocuments(response.documents);
    } catch (err: any) {
      console.error('Failed to load documents:', err);
      setError(err.message || 'Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file extension
    const ext = '.' + file.name.split('.').pop()?.toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      setError(`File type ${ext} not allowed. Allowed types: ${ALLOWED_EXTENSIONS.join(', ')}`);
      return;
    }

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      setError(`File size (${(file.size / (1024 * 1024)).toFixed(2)} MB) exceeds maximum allowed size of 10 MB`);
      return;
    }

    setError(null);
    setSelectedFile(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    try {
      setUploading(true);
      setError(null);
      await TransactionDocumentsAPI.uploadDocument(recordId, selectedFile, description || undefined);
      setSelectedFile(null);
      setDescription('');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      await loadDocuments();
    } catch (err: any) {
      setError(err.message || 'Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;

    try {
      setDeleting(true);
      setError(null);
      await TransactionDocumentsAPI.deleteDocument(recordId, deleteTarget.id);
      setDeleteTarget(null);
      await loadDocuments();
    } catch (err: any) {
      setError(err.message || 'Failed to delete document');
    } finally {
      setDeleting(false);
    }
  };

  const handlePreview = (doc: TransactionDocument) => {
    const url = TransactionDocumentsAPI.getPreviewUrl(recordId, doc.id);
    // Open in new tab with auth header (for images/pdfs that browser can display)
    window.open(`${url}?token=${token}`, '_blank');
  };

  const handleDownload = (doc: TransactionDocument) => {
    const url = TransactionDocumentsAPI.getDownloadUrl(recordId, doc.id);
    // Create a temporary link with auth
    const link = document.createElement('a');
    link.href = `${url}?token=${token}`;
    link.download = doc.original_filename;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleString();
  };

  const getFileIcon = (ext: string) => {
    const iconMap: Record<string, string> = {
      '.pdf': 'PDF',
      '.doc': 'DOC',
      '.docx': 'DOC',
      '.xls': 'XLS',
      '.xlsx': 'XLS',
      '.txt': 'TXT',
      '.csv': 'CSV',
      '.jpg': 'IMG',
      '.jpeg': 'IMG',
      '.png': 'IMG',
      '.gif': 'IMG',
    };
    return iconMap[ext.toLowerCase()] || 'FILE';
  };

  const getFileIconColor = (ext: string) => {
    const colorMap: Record<string, string> = {
      '.pdf': '#dc3545',
      '.doc': '#0d6efd',
      '.docx': '#0d6efd',
      '.xls': '#198754',
      '.xlsx': '#198754',
      '.txt': '#6c757d',
      '.csv': '#198754',
      '.jpg': '#0dcaf0',
      '.jpeg': '#0dcaf0',
      '.png': '#0dcaf0',
      '.gif': '#0dcaf0',
    };
    return colorMap[ext.toLowerCase()] || '#6c757d';
  };

  if (!isOpen) return null;

  return (
    <div className="document-dialog-overlay" onClick={onClose}>
      <div className="document-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="document-dialog-header">
          <h3>Documents {recordTitle && `- ${recordTitle}`}</h3>
          <button className="close-btn" onClick={onClose}>&times;</button>
        </div>

        {error && (
          <div className="document-error">
            {error}
            <button className="dismiss-error" onClick={() => setError(null)}>&times;</button>
          </div>
        )}

        {/* Upload Form (hidden for view-only users) */}
        {!user?.view_only && <div className="upload-section">
          <h4>Upload New Document</h4>
          <div className="upload-form">
            <input
              ref={fileInputRef}
              type="file"
              accept={ALLOWED_EXTENSIONS.join(',')}
              onChange={handleFileSelect}
              disabled={uploading}
            />
            {selectedFile && (
              <>
                <div className="selected-file">
                  <span>{selectedFile.name}</span>
                  <span className="file-size">({formatFileSize(selectedFile.size)})</span>
                </div>
                <input
                  type="text"
                  placeholder="Description (optional)"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  disabled={uploading}
                  className="description-input"
                />
                <button
                  className="btn btn-primary"
                  onClick={handleUpload}
                  disabled={uploading}
                >
                  {uploading ? 'Uploading...' : 'Upload'}
                </button>
              </>
            )}
          </div>
          <p className="upload-hint">
            Allowed types: {ALLOWED_EXTENSIONS.join(', ')} | Max size: 10 MB
          </p>
        </div>}

        {/* Documents List */}
        <div className="documents-section">
          <h4>Attached Documents ({documents.length})</h4>
          {loading ? (
            <div className="documents-loading">Loading documents...</div>
          ) : documents.length === 0 ? (
            <div className="no-documents">No documents attached yet.</div>
          ) : (
            <div className="documents-list">
              {documents.map((doc) => (
                <div key={doc.id} className="document-item">
                  <div className="document-icon" style={{ backgroundColor: getFileIconColor(doc.file_extension) }}>
                    {getFileIcon(doc.file_extension)}
                  </div>
                  <div className="document-info">
                    <div className="document-name">{doc.original_filename}</div>
                    <div className="document-meta">
                      <span>{formatFileSize(doc.file_size)}</span>
                      <span className="separator">|</span>
                      <span>Uploaded by {doc.uploader_name}</span>
                      <span className="separator">|</span>
                      <span>{formatDate(doc.created_at)}</span>
                    </div>
                    {doc.description && (
                      <div className="document-description">{doc.description}</div>
                    )}
                  </div>
                  <div className="document-actions">
                    {doc.can_preview && (
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => handlePreview(doc)}
                        title="Preview"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                          <circle cx="12" cy="12" r="3"/>
                        </svg>
                      </button>
                    )}
                    {doc.can_download && (
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => handleDownload(doc)}
                        title="Download"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                          <polyline points="7 10 12 15 17 10"/>
                          <line x1="12" y1="15" x2="12" y2="3"/>
                        </svg>
                      </button>
                    )}
                    {doc.can_delete && (
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => setDeleteTarget(doc)}
                        title="Delete"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="3 6 5 6 21 6"/>
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Delete confirmation */}
        {deleteTarget && (
          <div
            className="confirm-overlay"
            onClick={() => !deleting && setDeleteTarget(null)}
          >
            <div className="confirm-dialog" onClick={(e) => e.stopPropagation()}>
              <h4>Delete document?</h4>
              <p>
                Are you sure you want to delete{' '}
                <strong>{deleteTarget.original_filename}</strong>? This action cannot be undone.
              </p>
              <div className="confirm-actions">
                <button
                  className="btn btn-secondary"
                  onClick={() => setDeleteTarget(null)}
                  disabled={deleting}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-danger"
                  onClick={handleConfirmDelete}
                  disabled={deleting}
                >
                  {deleting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}

        <style>{`
          .document-dialog-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.5);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 1000;
          }

          .document-dialog {
            background: var(--bg-card);
            border-radius: 12px;
            width: 90%;
            max-width: 700px;
            max-height: 85vh;
            display: flex;
            flex-direction: column;
            box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
          }

          .confirm-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.55);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 1100;
          }

          .confirm-dialog {
            background: var(--bg-card);
            border-radius: 12px;
            width: 90%;
            max-width: 420px;
            padding: 1.5rem;
            box-shadow: 0 20px 60px rgba(0, 0, 0, 0.35);
          }

          .confirm-dialog h4 {
            margin: 0 0 0.75rem 0;
            color: var(--text-primary);
            font-size: 1.05rem;
          }

          .confirm-dialog p {
            margin: 0 0 1.25rem 0;
            color: var(--text-secondary);
            font-size: 0.9rem;
            line-height: 1.5;
          }

          .confirm-dialog strong {
            color: var(--text-primary);
            word-break: break-word;
          }

          .confirm-actions {
            display: flex;
            justify-content: flex-end;
            gap: 0.5rem;
          }

          .document-dialog-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 1rem 1.5rem;
            border-bottom: 1px solid var(--border-primary);
          }

          .document-dialog-header h3 {
            margin: 0;
            color: var(--text-primary);
            font-size: 1.125rem;
          }

          .close-btn {
            background: none;
            border: none;
            font-size: 1.5rem;
            color: var(--text-secondary);
            cursor: pointer;
            padding: 0;
            line-height: 1;
          }

          .close-btn:hover {
            color: var(--text-primary);
          }

          .document-error {
            margin: 1rem 1.5rem 0;
            padding: 0.75rem 1rem;
            background: var(--danger-light);
            border: 1px solid var(--danger);
            border-radius: 6px;
            color: var(--danger);
            font-size: 0.875rem;
            display: flex;
            justify-content: space-between;
            align-items: center;
          }

          .dismiss-error {
            background: none;
            border: none;
            color: var(--danger);
            font-size: 1.25rem;
            cursor: pointer;
            padding: 0;
            line-height: 1;
          }

          .upload-section {
            padding: 1rem 1.5rem;
            border-bottom: 1px solid var(--border-primary);
          }

          .upload-section h4 {
            margin: 0 0 0.75rem 0;
            color: var(--text-primary);
            font-size: 0.9rem;
            font-weight: 600;
          }

          .upload-form {
            display: flex;
            flex-direction: column;
            gap: 0.5rem;
          }

          .upload-form input[type="file"] {
            padding: 0.5rem;
            border: 2px dashed var(--border-primary);
            border-radius: 6px;
            background: var(--bg-secondary);
            cursor: pointer;
          }

          .upload-form input[type="file"]:hover {
            border-color: var(--primary);
          }

          .selected-file {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            padding: 0.5rem;
            background: var(--primary-light);
            border-radius: 4px;
            font-size: 0.875rem;
            color: var(--primary);
          }

          .file-size {
            color: var(--text-secondary);
          }

          .description-input {
            padding: 0.5rem 0.75rem;
            border: 1px solid var(--border-primary);
            border-radius: 4px;
            background: var(--bg-card);
            color: var(--text-primary);
            font-size: 0.875rem;
          }

          .description-input:focus {
            outline: none;
            border-color: var(--primary);
          }

          .upload-hint {
            margin: 0.5rem 0 0;
            font-size: 0.75rem;
            color: var(--text-muted);
          }

          .documents-section {
            flex: 1;
            overflow-y: auto;
            padding: 1rem 1.5rem;
          }

          .documents-section h4 {
            margin: 0 0 0.75rem 0;
            color: var(--text-primary);
            font-size: 0.9rem;
            font-weight: 600;
          }

          .documents-loading,
          .no-documents {
            padding: 2rem;
            text-align: center;
            color: var(--text-secondary);
            font-size: 0.875rem;
            background: var(--bg-secondary);
            border-radius: 8px;
            border: 1px dashed var(--border-primary);
          }

          .documents-list {
            display: flex;
            flex-direction: column;
            gap: 0.75rem;
          }

          .document-item {
            display: flex;
            align-items: flex-start;
            gap: 0.75rem;
            padding: 0.75rem;
            background: var(--bg-secondary);
            border: 1px solid var(--border-primary);
            border-radius: 8px;
          }

          .document-icon {
            width: 40px;
            height: 40px;
            border-radius: 6px;
            display: flex;
            align-items: center;
            justify-content: center;
            color: white;
            font-size: 0.7rem;
            font-weight: 700;
            flex-shrink: 0;
          }

          .document-info {
            flex: 1;
            min-width: 0;
          }

          .document-name {
            font-weight: 500;
            color: var(--text-primary);
            font-size: 0.9rem;
            word-break: break-word;
          }

          .document-meta {
            font-size: 0.75rem;
            color: var(--text-secondary);
            margin-top: 0.25rem;
          }

          .document-meta .separator {
            margin: 0 0.5rem;
          }

          .document-description {
            margin-top: 0.25rem;
            font-size: 0.8rem;
            color: var(--text-secondary);
            font-style: italic;
          }

          .document-actions {
            display: flex;
            gap: 0.5rem;
            flex-shrink: 0;
          }

          .document-actions .btn-sm {
            padding: 0.375rem 0.5rem;
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .btn {
            padding: 0.5rem 1rem;
            border-radius: 6px;
            font-size: 0.875rem;
            font-weight: 500;
            cursor: pointer;
            border: none;
            transition: all 0.2s ease;
          }

          .btn-primary {
            background: var(--primary);
            color: white;
          }

          .btn-primary:hover {
            background: var(--primary-dark);
          }

          .btn-primary:disabled {
            opacity: 0.6;
            cursor: not-allowed;
          }

          .btn-secondary {
            background: var(--bg-card);
            border: 1px solid var(--border-primary);
            color: var(--text-primary);
          }

          .btn-secondary:hover {
            background: var(--bg-hover);
          }

          .btn-danger {
            background: var(--danger);
            color: white;
          }

          .btn-danger:hover {
            background: var(--danger-dark);
          }

          .btn-sm {
            padding: 0.25rem 0.5rem;
            font-size: 0.8rem;
          }
        `}</style>
      </div>
    </div>
  );
};
